/**
 * useIdleTimer.js — Detects user inactivity and fires callbacks.
 *
 * How it works:
 *   - Listens to mouse, keyboard, touch, and scroll events on the document.
 *   - Resets a countdown timer on every event.
 *   - When countdown reaches zero, fires onIdle().
 *   - When user returns from idle, fires onActive() and resets.
 *
 * @param {object} options
 * @param {number}   options.idleTimeout    - ms of inactivity before onIdle fires (default 15 min)
 * @param {number}   options.warningTimeout - ms before idleTimeout when onWarning fires (default 2 min)
 * @param {function} options.onIdle         - called when user goes idle
 * @param {function} options.onWarning      - called when warning countdown starts
 * @param {function} options.onActive       - called when user returns from idle/warning
 * @param {boolean}  options.enabled        - pause the timer when false (e.g. when logged out)
 */

import { useEffect, useRef, useCallback } from 'react';

// Events that count as "user is active"
const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown',
  'touchstart', 'touchmove', 'scroll', 'wheel', 'click',
];

export function useIdleTimer({
  idleTimeout    = 15 * 60 * 1000,  // 15 minutes
  warningTimeout = 2  * 60 * 1000,  // warn 2 minutes before logout
  onIdle         = () => {},
  onWarning      = () => {},
  onActive       = () => {},
  enabled        = true,
} = {}) {

  // Track current phase so callbacks only fire on phase transitions
  const phase       = useRef('active'); // 'active' | 'warning' | 'idle'
  const idleTimer   = useRef(null);
  const warnTimer   = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
  }, []);

  const resetTimers = useCallback(() => {
    if (!enabled) return;

    clearTimers();

    // If we were in warning or idle, transition back to active
    if (phase.current !== 'active') {
      phase.current = 'active';
      onActive();
    }

    // Set warning timer: fires (idleTimeout - warningTimeout) ms from now
    const timeUntilWarning = idleTimeout - warningTimeout;
    if (timeUntilWarning > 0) {
      warnTimer.current = setTimeout(() => {
        phase.current = 'warning';
        onWarning();
      }, timeUntilWarning);
    }

    // Set idle timer: fires idleTimeout ms from now
    idleTimer.current = setTimeout(() => {
      phase.current = 'idle';
      onIdle();
    }, idleTimeout);
  }, [enabled, idleTimeout, warningTimeout, onIdle, onWarning, onActive, clearTimers]);

  // ── Mount: attach event listeners and start timer ────────────────────────────
  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Start initial countdown
    resetTimers();

    // Attach activity listeners with passive flag for scroll/touch performance
    const opts = { passive: true };
    ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, resetTimers, opts));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((ev) => document.removeEventListener(ev, resetTimers, opts));
    };
  }, [enabled, resetTimers, clearTimers]);
}
